/**
 * §7's breakdown panel as rules, shared by room-web's standings screen and the player portal.
 *
 * <b>Every decision in here fails silently, and one of them fails in the way §7 spends a paragraph
 * forbidding.</b> A dropped result that goes missing looks exactly like a result that was never
 * recorded, and the only reader who could tell is the player who cashed in it — which is why §7 says
 * a player who sees no trace of it "will report a bug". So the rule about which rows are listed and
 * which are collapsed lives here, with tests, rather than as two `filter` calls in two components.
 *
 * <b>It computes nothing about a rating.</b> Every figure is the server's, frozen into the snapshot
 * the panel is reading; what this file decides is which rows to show, which figure to put beside
 * which, and what to say about the rows it did not show.
 *
 * <b>Shared across both apps, unlike `LeaderboardBreakdown`'s layout — and the split is the same one
 * `memberships.ts` draws.</b> Wording is per-reader when it <i>describes a person</i>: an operator
 * being told what evidence a room holds about somebody else is not the same sentence as a player
 * being told how they proved something. These sentences describe §3.3's <i>rule</i> — what happened
 * to a row, and why — which is the same claim whoever reads it. Two copies of that would drift, and
 * the drift would land on the most important sentence in the panel.
 */

/** One result inside a period, as §7 shapes it. Declared structurally; the apps pass the DTO in. */
export interface RatingResultRow {
  eventId: string;
  eventName: string;
  eventDate: string;
  eventScore: number;
  decayedScore: number;
  selected: boolean;
  selectionNote: string | null;
}

/** One period, carrying its own bounds so a reader can check §3.2's bucketing. */
export interface RatingPeriodRow<R extends RatingResultRow = RatingResultRow> {
  index: number;
  from: string | null;
  to: string;
  n: number | null;
  decay: number;
  contribution: number;
  results: readonly R[];
}

export interface RatingPeriodView<R extends RatingResultRow> {
  /**
   * The rows to list, in the order the server sent them — <b>best raw score first</b>, which is
   * §3.3's own selection order, so the cut line is visible where it falls rather than having to be
   * worked out. Never re-sorted here: a panel that ordered by date would move the cut into the
   * middle of the list and make the dropped rows look arbitrary.
   */
  rows: readonly R[];

  /** Listed rows that survived §3.3's cut. */
  selected: number;

  /**
   * Listed rows the cut dropped — the ones §7 is about. A player cashed in each of these, and each
   * carries the engine's own reason.
   */
  dropped: number;

  /** Rows collapsed to a count rather than listed. See {@link ratingPeriodView}. */
  scoredNothing: number;
}

/**
 * §7's rows for one period: what to list, and how many were collapsed.
 *
 * <b>A result that scored NOTHING is collapsed to a count; everything else is listed.</b> The two
 * halves of that are separate rules and neither may be dropped:
 *
 * - <b>Listed, always:</b> every result that scored something, selected or not. §7 is explicit that
 *   a dropped result is present with `selected: false` and its note, never omitted.
 * - <b>Collapsed:</b> results scoring exactly zero, which §3.3 keeps out of selection entirely. On
 *   imported data these are overwhelmingly <i>synthetic</i> non-cashing rows (§12), so listing each
 *   one as a line item would show a player a dozen events they may never have sat down at — and
 *   there is nothing to explain about any of them beyond the one sentence the count carries.
 *
 * <b>Keyed on the score and never on the note.</b> The server writes two different sentences for
 * these two cases and matching on the text of one would be a client re-deriving a server rule
 * through its wording — which breaks the first time somebody edits a sentence.
 *
 * <b>`!selected` is belt as well as braces.</b> §3.3 makes a zero-score result unselectable, so the
 * score alone would do; requiring both means a zero that somehow <i>was</i> selected is listed
 * rather than silently swallowed, and listing is the direction that cannot hide anything. Note the
 * rule is exactly zero and not "non-positive": a <b>negative</b> result competed and lost, carries
 * the cut's sentence, and is listed like any other (§3.3).
 */
export function ratingPeriodView<R extends RatingResultRow>(
  period: RatingPeriodRow<R>,
): RatingPeriodView<R> {
  const rows: R[] = [];
  let scoredNothing = 0;
  let selected = 0;
  let dropped = 0;

  for (const result of period.results) {
    if (!result.selected && result.eventScore === 0) {
      scoredNothing += 1;
      continue;
    }

    rows.push(result);
    if (result.selected) selected += 1;
    else dropped += 1;
  }

  return { rows, selected, dropped, scoredNothing };
}

/**
 * The figure to print beside a row's raw score, or null when there is nothing to add.
 *
 * <b>§7 puts `decayedScore` on the row so that "why did this contribute 45 instead of 50" is
 * answered where it is asked.</b> Two cases produce a figure and they are the two a reader asks
 * about: age took something off it, or the cut took all of it. A row whose decayed score equals its
 * raw score has neither happened to it, and printing `50 → 50` there spends a reader's attention on
 * the case that needs none — the same rule the engine already applies to a multiplier of exactly 1.
 *
 * <b>Zero is a figure and not an absence.</b> A dropped row scoring 80 answers `0`, which is the
 * whole point: that is the number that explains why the total is not 80 higher.
 *
 * <b>THIS DECIDES WHETHER THERE IS A FIGURE. IT DOES NOT DECIDE THE SENTENCE, AND THE TWO PANELS
 * DELIBERATELY WORD IT DIFFERENTLY — do not "fix" them into agreement.</b>
 *
 * <pre>
 * room-web    contributed 2.44 of 3.05
 * player-web  Counted 2.44 of 3.05 — this period is worth less because it is older.
 * </pre>
 *
 * Same fact, two readers. <b>The operator configured the ramp; the player did not.</b> An operator
 * set `decay: 0.8` on period 3 and is checking arithmetic against a table they wrote, so the figures
 * alone are the answer and a clause explaining decay is noise on every row of a long panel. A player
 * has never seen the ramp, cannot see it, and has no way to discover that a result is worth less for
 * being older — so a bare pair of numbers reads as an error in their favour that somebody took back.
 *
 * The rule is the one this repository keeps everywhere a fact reaches two audiences: <b>share what
 * decides, split what speaks.</b> `memberships.ts` deliberately does not share its wording with
 * player-web's `methodLabel` over the same column — an operator is told what evidence the room
 * holds, a player is told how they proved it — and `LeaderboardBreakdown` stays in player-web for
 * the same reason. What is shared here is {@link ratingBreakdownRows}, {@link decayedFigure} and
 * {@link scoredNothingNote}: which rows appear, whether a figure is due, and the rules §7 fixes.
 * Unifying the sentences would mean one of the two audiences reading the other's.
 */
export function decayedFigure(result: RatingResultRow): number | null {
  return result.decayedScore === result.eventScore ? null : result.decayedScore;
}

/**
 * A period's own date range, so a reader can check §3.2's bucketing without knowing the anchor
 * arithmetic.
 *
 * <b>`from` is already the first INCLUDED date</b> — the server converts §3.2's exclusive lower edge
 * on the way out, because printing the exclusive bound would tell a reader that an event on that
 * date belongs to this period when §3.2 puts it in the older one. Nothing here adjusts it.
 *
 * <b>Deliberately not `toLocaleDateString`.</b> These bounds exist to be checked against a date on a
 * schedule, and `03/04/2026` is two different days depending on who is reading — the ambiguity this
 * repository refuses everywhere a date is entered, and there is no reason to admit it on the way
 * out. A named month cannot be misread.
 */
export function periodRangeLabel(from: string | null, to: string): string {
  return from === null ? `on or before ${day(to)}` : `${day(from)} – ${day(to)}`;
}

/** `2026-03-31` as `31 Mar 2026`, and unchanged if it is not a date at all. */
function day(iso: string): string {
  const match = /^(\d{4})-(\d{2})-(\d{2})$/.exec(iso);
  if (match === null) return iso;

  const month = MONTHS[Number(match[2]) - 1];
  if (month === undefined) return iso;

  return `${Number(match[3])} ${month} ${match[1]}`;
}

const MONTHS = [
  'Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun',
  'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec',
];

/**
 * §4.5's `n` for one period, as a reader meets it.
 *
 * Singular is written out rather than assembled, for the reason the engine's own selection note is:
 * "best 1 results" is the kind of sentence that makes a reader distrust the rest of the screen.
 */
export function periodCutLabel(n: number | null): string {
  if (n === null) return 'every result counts';
  if (n === 1) return 'best result counts';
  return `best ${n} count`;
}

/**
 * A period's decay, in the vocabulary §9.3's period table already uses — "full weight" and "worth
 * less" rather than a bare multiplier.
 *
 * A factor of exactly 1 says "full weight" rather than "× 1", which is the same omission rule the
 * engine applies to a neutral multiplier: a reader asked to interpret `× 1` asks where it came from,
 * and the answer is nowhere.
 */
export function periodWeightLabel(decay: number): string {
  if (decay === 1) return 'full weight';
  return `${trimPercent(decay * 100)}% weight`;
}

/** `75` rather than `75.00000000000001`, which is what `0.75 * 100` can produce. */
function trimPercent(value: number): string {
  return String(Math.round(value * 100) / 100);
}

/**
 * The sentence standing in for the rows {@link ratingPeriodView} collapsed, or null when it
 * collapsed none.
 *
 * <b>It says what they are, and the wording has to be true of two quite different kinds of row.</b>
 * In a room's own data a non-cashing entry is genuine history — somebody sat down and busted. In
 * imported data the same row is very often <i>synthetic</i> (§12): a field size reconstructed from a
 * published total, with no claim that any particular person played. So the sentence describes the
 * <b>result</b> and never the player's afternoon: it does not say they entered, and it does not say
 * they played.
 *
 * <b>Silence is what §7 forbids in the same breath as omitting a dropped row</b> — hence a sentence
 * rather than simply leaving them out.
 */
export function scoredNothingNote(count: number): string | null {
  if (count <= 0) return null;

  return count === 1
    ? 'One more result scored nothing — an unpaid place, or an entry recorded without a finish — ' +
        'so it did not compete for a place.'
    : `${count} more results scored nothing — unpaid places, or entries recorded without a ` +
        'finish — so none of them competed for a place.';
}

/**
 * §7's `tailDropped`, stated.
 *
 * <b>These belong to no period, so §7's shape has nowhere to list them</b> — they can be reported or
 * they can be silent, and §7 forbids the silence. Always 0 under a rolling window, whose periods
 * tile it exactly; reachable under `fixed` and `allTime`.
 */
export function tailDroppedNote(count: number): string | null {
  if (count <= 0) return null;

  return count === 1
    ? 'One more result is inside this rating’s window but older than its last period, so it scores ' +
        'nothing.'
    : `${count} more results are inside this rating’s window but older than its last period, so ` +
        'they score nothing.';
}
